import { useQuery } from '@apollo/client';

import { UserFlagsDisplayFlag } from '@xing-com/crate-common-graphql-types';
import { useFeature, useViewerData } from '@xing-com/hub';

import { SelfUserFlagQueryDocument } from '../queries/self-user-flag-query.gql-types';
import { UserFlagQueryDocument } from '../queries/user-flag-query.gql-types';

import { FlagContent } from './flag-content';
import { FlagError } from './flag-error';
import { FlagLoading } from './flag-loading';
import { trackCtaUrl } from './track-cta-url';

export type UserFlagContentProps = {
  premiumTheme: boolean;
  show: boolean;
  userId: string;
  displayFlag: UserFlagsDisplayFlag;
};

export const UserFlagContent: React.FC<UserFlagContentProps> = ({
  premiumTheme,
  show,
  userId,
  displayFlag,
}) => {
  const user = useViewerData().data?.user;
  const viewerUserId = user?.id;
  const viewerDisplayFlag =
    user?.userFlags?.displayFlag || UserFlagsDisplayFlag.Basic;
  const isPremium = useFeature('isPremium', false);

  const selfQuery = viewerUserId === userId;

  const {
    data: dataSelf,
    loading: loadingSelf,
    error: errorSelf,
  } = useQuery(SelfUserFlagQueryDocument, {
    skip: !show || !selfQuery,
    variables: { displayFlag: viewerDisplayFlag },
  });

  const {
    data: dataOther,
    loading: loadingOther,
    error: errorOther,
  } = useQuery(UserFlagQueryDocument, {
    skip: !show || selfQuery,
    variables: {
      otherDisplayFlag: displayFlag,
      otherUserId: userId?.toString(),
      displayFlag: viewerDisplayFlag,
    },
  });

  const userFlagInfo = selfQuery
    ? dataSelf?.viewer?.selfUserFlagDetails || {}
    : dataOther?.viewer?.otherUserFlagDetails || {};

  const loading = selfQuery ? loadingSelf : loadingOther;
  const error = selfQuery ? errorSelf : errorOther;

  return (
    <>
      {loading ? (
        <FlagLoading />
      ) : error ? (
        <FlagError />
      ) : (
        <FlagContent
          headerCopy={userFlagInfo?.title || ''}
          bodyCopy={userFlagInfo?.body?.filter((copy) => copy !== null) || []}
          ctaCopy={userFlagInfo?.action?.text}
          ctaUrl={trackCtaUrl({
            url: userFlagInfo?.action?.url,
            isPremium,
            displayFlag,
          })}
          premiumTheme={premiumTheme}
        />
      )}
    </>
  );
};
